import React, { useRef, useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { isArray } from 'lodash';
import PropTypes from 'prop-types';

import { Grid, Modal, DialogFullScreen } from '~/components';
import DialogArqueacaoAutomatica from './components/DialogArqueacaoAutomatica';
import { MODULO_ARQUEACAO_AUTOMATICA } from '~/constants/modules';
import { t, can } from '~/helpers';
import api from '~/services/api';
import {
  estoqueTanquesInRequest,
  recordFiltering,
  resetFiltering,
} from '~/store/modules/operacaoestoquetanque/actions';

import {
  Graph,
  Alarms,
  GraphicModal,
  Maintenance,
  Historic,
} from './components';

// const Gridh = lazy(() => import('~/components/Grid'));

export default function OperacaoEstoqueTanque({ hidden, limit, unique }) {
  const estoqueTanque = useSelector((state) => state.operacaoestoquetanque);
  const [atualizandoArqueacao, setAtualizandoArqueacao] = useState(false);
  const [codigoTanqueArqueacao, setCodigoTanqueArqueacao] = useState('');

  const dispatch = useDispatch();

  const dialog = useRef();
  const graphicRef = useRef();
  const maintenanceRef = useRef(null);
  const arqueacaoAutoRef = useRef(null);
  const confirmUpdateArqueacaoRef = useRef(null);
  const [
    openingArqueacaoAutomaticaModal,
    setOpeningArqueacaoAutomaticaModal,
  ] = useState(false);
  const [canUpdateArqueacao, setCanUpdateArqueacao] = React.useState(false);
  const [arqueacaoModalOpened, setArqueacaoModalOpened] = React.useState(false);

  const modalRef = useRef(null);

  const handleRefreshRecors = useCallback(() => {
    const { page, params } = estoqueTanque;

    dispatch(recordFiltering({ ...params, page, pagesize: limit }));
  }, [estoqueTanque, limit]);

  const handleOpenHistories = useCallback((params) => {
    modalRef.current.handleOpenModal({ data: params });
  }, []);

  const actionsArqueacao = useMemo(
    () => [
      {
        label: t('s.geral.aprovar'),
        callback: (data) =>
          confirmUpdateArqueacaoRef.current.handleOpenModal({ data }),
        color: 'primary',
        autoFocus: false,
        disabled: !canUpdateArqueacao,
        variant: 'contained',
      },
      {
        label: t('s.geral.botaovoltar'),
        callback: () => arqueacaoAutoRef.current.handleCloseModal(),
        color: 'default',
        autoFocus: false,
        variant: 'contained',
      },
    ],
    [canUpdateArqueacao]
  );

  const confirmationCloseModalsArqueacao = () => {
    confirmUpdateArqueacaoRef.current.handleCloseModal();
    arqueacaoAutoRef.current.handleCloseModal();
  };

  const handleOpenArqueacaoAutomatica = useCallback((tank) => {
    if (arqueacaoModalOpened) {
      return;
    }

    setCodigoTanqueArqueacao(tank.tafi_codigo);
    setOpeningArqueacaoAutomaticaModal(true);

    arqueacaoAutoRef.current.handleOpenModal({
      data: {
        idhistoricoarqaut: tank.idhistoricoarqaut,
      },
      setCanUpdateArqueacao,
    });

    setArqueacaoModalOpened(true);
    setOpeningArqueacaoAutomaticaModal(false);
  }, []);

  const handleUpdateArqueacao = useCallback(async (id) => {
    if (atualizandoArqueacao) {
      return;
    }

    setAtualizandoArqueacao(true);

    try {
      await api.put(`historicoarqaut/atualiza/${id}`);
      toast.success(t('s.operacao.arqueacaoautomatica.atualizada'));
      confirmationCloseModalsArqueacao();
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    } finally {
      setAtualizandoArqueacao(false);
    }
  }, []);

  return (
    <div style={{ flexGrow: 1 }}>
      <Grid
        state={estoqueTanque}
        read={estoqueTanquesInRequest}
        filter={recordFiltering}
        reset={resetFiltering}
        timeOut={30000}
        limit={limit}
        unique={unique}
        component={(props) => (
          <Graph
            {...props}
            filter={recordFiltering}
            alarms={(param) => dialog.current.handleToggleModal(param)}
            graphics={(param) => graphicRef.current.handleToggleModal(param)}
            maintenance={(tank) =>
              maintenanceRef.current.handleOpenModal({ tank })
            }
            onCallback={handleOpenHistories}
            canOpenArqueacaoAutomaticaModal={!openingArqueacaoAutomaticaModal}
            openArqueacaoAutomaticaModal={handleOpenArqueacaoAutomatica}
          />
        )}
        hidden={hidden}
        sidebar={[
          {
            type: 'autocomplete',
            name: 'idposto',
            label: t('c.menu.stock.posto'),
            param: {
              url: 'postoscomboios',
              unique: 'idposto',
              label: 'codigonomeposto',
            },
          },
          {
            type: 'autocomplete',
            name: 'idproduto',
            label: t('c.menu.stock.produto'),
            param: {
              url: 'produtos',
              unique: 'idproduto',
              label: 'nomeproduto',
            },
          },
          {
            type: 'select',
            name: 'countalarmestanque',
            label: t('c.menu.alarme.titulo'),
            options: [
              { label: t('s.geral.opcoes.sim'), value: '1' },
              { label: t('s.geral.opcoes.nao'), value: '0' },
            ],
            ruleType: (value) =>
              value === '0' ? 'MENOR_IGUAL' : 'MAIOR_IGUAL',
          },
          {
            type: 'select',
            name: 'alertaressuprimentotanquefisico',
            label: t('c.menu.stock.alartaderesuprimento'),
            options: [
              { label: t('s.geral.opcoes.sim'), value: '1' },
              { label: t('s.geral.opcoes.nao'), value: '0' },
            ],
            ruleType: (value) =>
              value === '0' ? 'MENOR_IGUAL' : 'MAIOR_IGUAL',
          },
          {
            type: 'select',
            name: 'volumeespacovaziotanquefisico',
            label: t('s.operacao.recebimentonf.espacovazio'),
            options: [
              { label: t('s.geral.opcoes.sim'), value: '1' },
              { label: t('s.geral.opcoes.nao'), value: '0' },
            ],
            ruleType: (value) =>
              value === '0' ? 'MENOR_IGUAL' : 'MAIOR_IGUAL',
          },
          ...(can(MODULO_ARQUEACAO_AUTOMATICA)
            ? [
              {
                type: 'checkbox',
                name: 'idhistoricoarqaut',
                label: t('s.menu.configabastek.arqueacaoautomatica'),
                checkedValue: 1,
                ruleType: () => 'MAIOR_IGUAL',
              },
            ]
            : []),
        ]}
      />
      <Alarms ref={dialog} />
      <GraphicModal ref={graphicRef} />
      <DialogFullScreen
        ref={modalRef}
        title={t('s.relatorio.dvecdanificadoparado.tipohistorico')}
        component={Historic}
      />

      <Modal
        ref={maintenanceRef}
        form
        disableBackdropClick
        disableEscapeKeyDown
        title={t('c.menu.stock.acaomanutencao')}
        maxWidth="xs"
        component={Maintenance}
        onClose={handleRefreshRecors}
      />

      <Modal
        ref={arqueacaoAutoRef}
        loading={openingArqueacaoAutomaticaModal}
        disableBackdropClick
        disableEscapeKeyDown
        component={DialogArqueacaoAutomatica}
        labelCancell={t('s.geral.botaovoltar')}
        title={`${t('s.operacao.arqueacaoautomatica.atualizacao')}${codigoTanqueArqueacao ? ` (${codigoTanqueArqueacao})` : ''}`}
        actions={actionsArqueacao}
        onClose={() => setArqueacaoModalOpened(false)}
      />

      <Modal
        ref={confirmUpdateArqueacaoRef}
        disableBackdropClick
        disableEscapeKeyDown
        component={() => <p>{t('s.geral.acaonaopoderaserdesfeita')}</p>}
        title={t('s.geral.aprovararqueacaoautomatica')}
        maxWidth="xs"
        loading={atualizandoArqueacao}
        actions={[
          {
            label: t('s.geral.aprovar'),
            autoFocus: true,
            variant: 'outlined',
            color: 'primary',
            disabled: atualizandoArqueacao,
            callback: (data) => handleUpdateArqueacao(data.idhistoricoarqaut),
          },
          {
            label: t('s.geral.botaovoltar'),
            callback: () =>
              confirmUpdateArqueacaoRef.current.handleCloseModal(),
            variant: 'outlined',
            disabled: atualizandoArqueacao,
            color: 'secondary',
          },
        ]}
      />
    </div>
  );
}

OperacaoEstoqueTanque.propTypes = {
  hidden: PropTypes.bool,
  limit: PropTypes.number,
  unique: PropTypes.objectOf(PropTypes.any),
};

OperacaoEstoqueTanque.defaultProps = {
  hidden: false,
  limit: 6,
  unique: {},
};

